import { useCallback } from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import last from "lodash-es/last"

import { generateUuid, reduxBatch } from "@/3514/utils"

import { useCoachingChatSocketReceiver as useSocketReceiver } from "../api"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatMessageAuthor as EMessageAuthor,
    ECoachingChatSocketListenerEventName as ESocketListenerEventName,
    type TCoachingChatConversationMessage as TConversationMessage,
    type TCoachingChatSocketHistoryEventData as THistoryEventData,
    type TCoachingChatSocketHistoryEventResponse as THistoryEventResponse,
    type _TCoachingChatHookSharedParams as _TParams
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const {
    streamingUtils: { adaptHistoryMessageToUiMessages: historyMessagesAdapter }
} = uiUtils

function useHistoryReceiver({ entityId, entityMode, wasChatCompleted }: _TParams): void {
    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const isEnabled: boolean =
        eq(conversationApiMode, EConversationApiMode.Sockets) &&
        eq(entityMode, EEntityMode.Full) &&
        !wasChatCompleted &&
        isNumber(conversationId)

    const handler: (resp: THistoryEventData[]) => void = useCallback(
        (resp: THistoryEventData[]): void => {
            setConversationApiMode(EConversationApiMode.Sockets)

            const tempMessage: TConversationMessage = {
                id: 1,
                author: EMessageAuthor.AI,
                content: String(""),
                isStreamable: true,
                status: EConversationMessagePhase.Waiting,
                clientMessageId: generateUuid(),
                conversationId
            }

            reduxBatch((): void => {
                if (!isEmpty(resp)) {
                    conversationDispatcher.loadConversation({
                        summary: [],
                        status: EConversationStatus.InProgress,
                        isMatrixFeedbackCompleted: false,
                        id: conversationId,
                        completedAt: undefined,
                        messages: historyMessagesAdapter(resp, conversationId)
                    })

                    if (eq(last<THistoryEventData>(resp).role, EMessageAuthor.User)) {
                        conversationDispatcher.addMessage(tempMessage)
                        conversationDispatcher.markInput({ isDisabled: true, isDimmed: true })
                    } else conversationDispatcher.markInput({ isDisabled: false, isDimmed: false })
                } else
                    conversationDispatcher.loadConversation({
                        summary: [],
                        status: EConversationStatus.InProgress,
                        isMatrixFeedbackCompleted: false,
                        id: conversationId,
                        completedAt: undefined,
                        messages: [tempMessage]
                    })
            })
        },
        [conversationId, conversationDispatcher]
    )

    useSocketReceiver<THistoryEventResponse>({
        isEnabled,
        event: ESocketListenerEventName.History,
        handler
    })
}

export { useHistoryReceiver as useCoachingChatHistoryReceiver }
